import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/layout/Logo";
import ThemeToggle from "@/components/widgets/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (!r.ok) return toast.error(r.error);
    toast.success("Account created — let's verify your mobile.");
    nav("/app/apply");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden brand-gradient text-white">
        <div className="relative z-10"><Logo /></div>
        <div className="relative z-10 max-w-md space-y-6">
          <h2 className="font-display text-4xl leading-tight">Your loan journey starts in under 90 seconds.</h2>
          <p className="text-white/80">Create a Finzpe account, verify your mobile, complete KYC and choose the loan that fits your salary cycle.</p>
          <ul className="space-y-2 text-white/90 text-sm">
            <li>• Bank-grade encryption, PCI-aligned handling</li>
            <li>• Transparent fees, no hidden surprises</li>
            <li>• Real-time application status tracking</li>
          </ul>
        </div>
        <div className="hero-orb h-96 w-96 bg-white/30" style={{ top: "-4rem", left: "-4rem" }} />
        <div className="hero-orb h-72 w-72 bg-white/20" style={{ bottom: "-4rem", right: "-4rem" }} />
      </div>

      <div className="flex flex-col p-6 lg:p-10 max-w-md mx-auto w-full">
        <div className="flex items-center justify-between">
          <div className="lg:hidden"><Logo /></div>
          <div className="ml-auto"><ThemeToggle /></div>
        </div>
        <div className="flex-1 flex flex-col justify-center py-10">
          <h1 className="font-display text-3xl mb-2">Create your account</h1>
          <p className="text-sm text-muted-foreground mb-8">It only takes a minute.</p>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} required />
            </div>
            <div>
              <Label htmlFor="phone">Mobile</Label>
              <Input id="phone" data-testid="register-phone" placeholder="+91 XXXXXXXXXX" value={form.phone} onChange={set("phone")} />
            </div>
            <div>
              <Label htmlFor="password">Password (min 6 chars)</Label>
              <Input id="password" data-testid="register-password" type="password" value={form.password} onChange={set("password")} required minLength={6} />
            </div>
            <Button type="submit" disabled={loading} className="w-full brand-gradient text-white rounded-full h-11" data-testid="register-submit">{loading ? "Creating..." : "Create account"}</Button>
          </form>
          <div className="mt-6 text-sm text-muted-foreground">Already have an account? <Link to="/login" className="text-primary font-medium">Sign in</Link></div>
          <div className="mt-6 text-xs text-muted-foreground">By creating an account you agree to Finzpe's <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</div>
        </div>
      </div>
    </div>
  );
}
